/**
 * Settings-specific formatting kept pure and unit-testable: absolute dates
 * (`12 Mar 2026`), relative "last seen / last used" ages (`2 min ago`, `1 h ago`,
 * `3 d ago`, `Yesterday`, `now`), user-role display names, and the API-error
 * trace-id / abort helpers every Settings data hook shares.
 */
import { ApiError } from '../../api/errors';
import type { UserRole } from '../../api/schema';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Absolute day, `12 Mar 2026`, in UTC; an unparseable value passes through. */
export function fmtDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const month = MONTHS[date.getUTCMonth()] ?? '';
  return `${String(date.getUTCDate())} ${month} ${String(date.getUTCFullYear())}`;
}

/**
 * Relative age of a timestamp: `now` under a minute, then `{n} min ago`,
 * `{n} h ago`, `Yesterday`, then `{n} d ago`. A null/absent value renders `—`.
 */
export function relativeAge(iso: string | null | undefined, now: number = Date.now()): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const ms = now - then;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  return `${String(days)} d ago`;
}

const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System admin',
  partner_admin: 'Partner admin',
  tenant_admin: 'Tenant admin',
  tenant_analyst: 'Analyst',
  tenant_viewer: 'Viewer',
  ingestion_service: 'Ingestion service',
};

/** Human role name shown in the Users table and the invite/role controls. */
export function roleLabel(role: UserRole): string {
  return ROLE_LABELS[role];
}

/** The trace/correlation id carried on a backend problem-details error, if any. */
export function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const problem = error.problem;
    const id = problem.correlation_id ?? problem.trace_id ?? problem.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

/**
 * The server's own explanation for a failed write, or `fallback` when there is
 * none.
 *
 * A validation refusal (`422`) is the one failure the user can actually act on
 * — "these scopes cannot be granted", "that slug is taken" — so swallowing it
 * behind a generic "try again" leaves them retrying something that cannot
 * succeed. `ApiError.message` is already the problem-details `detail` (falling
 * back to `title`).
 *
 * Only a 4xx speaks: a 5xx means the server broke, and its title ("Internal
 * Server Error") describes nothing the user can change, so the caller's
 * fallback — which at least says what failed — is the better message.
 */
export function reasonOf(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  if (error.status < 400 || error.status >= 500) return fallback;
  return error.message.trim() === '' ? fallback : error.message;
}

/** True only for an AbortController-driven cancel of a superseded/unmounted load. */
export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Uppercase two-letter initials for an avatar, from a display name. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}
